
import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { Action, Getter } from 's-vuex-class'
import Modal from './Modal.vue'
import BaseButton from '@/components/BaseButton.vue'
import TextInput from './TextInput.vue'
import SelectInput from './SelectInput.vue'
import StripInput from './StripInput.vue'
import { IFilter, IDigest } from '@/entities'
import { StripInputOption, initDayOptions } from '@/entities/survey'

@Component<FilterAndDigestEditor>({
    components: { Modal, BaseButton, TextInput, SelectInput, StripInput },
})
export default class FilterAndDigestEditor extends Vue {
    public show = false
    public name = ''
    public is_shared = false
    public frequency = ''
    public day = '1'
    public dayOptions: StripInputOption[] = initDayOptions

    @Getter public filterEditMode
    @Getter public filterEditing
    @Getter public digestEditing

    @Action public setFilterEditing
    @Action public setDigestEditing
    @Action public createFilter
    @Action public patchFilter
    @Action public deleteFilter
    @Action public createDigest
    @Action public patchDigest
    @Action public deleteDigest
    @Action public statusAddToast

    /**
     * When filterEditing and digestEditing changed, update the local form values here.
     * They should only be changed when the modal is being opened and closed (set to null)
     */
    @Watch('filterEditing') public onFilterEditingChanged(filter?: IFilter) {
        if (filter) {
            this.is_shared = !!filter.is_shared
            this.name = filter.name || ''
        } else {
            this.is_shared = false
            this.name = ''
        }
    }

    @Watch('digestEditing') public onDigestEditingChanged(digest?: IDigest) {
        if (digest) {
            this.frequency = digest.frequency || ''
            this.day = digest.day || '1'
        } else {
            this.frequency = ''
            this.day = '1'
        }
    }

    public get readable(): string {
        if (!this.filterEditing) {
            return ''
        }

        const { filter_rules, time_unit, time_value } = this.filterEditing

        const s = String(time_value) !== '1'

        const timeStr =
            time_unit === 'custom'
                ? `From ${time_value.replace('_', ' - ')}`
                : `For the last ${s ? time_value : ''} ${time_unit}${
                      s ? 's' : ''
                  }`

        const ruleStr = filter_rules
            .filter(({ value }) => value[0] !== 'any')
            .map(
                ({ column, value }) =>
                    `${column
                        .replace('_c', '')
                        .replace('_', ' ')} = ${value.join(', ')}`
            )
            .join(' and ')

        return ruleStr.length > 0 ? `${timeStr} who have ${ruleStr}` : timeStr
    }

    public get stripDayOptions() {
        return this.dayOptions.map((dayOption) => {
            if (dayOption.value === this.day) {
                dayOption.selected = true
            } else {
                dayOption.selected = false
            }

            return dayOption
        })
    }

    public handleClose() {
        this.setFilterEditing({ id: -1, mode: 'closed' })
        this.setDigestEditing(-1)
    }

    public async handleSave() {
        const {
            name,
            is_shared,
            frequency,
            day,
            filterEditMode,
            filterEditing,
            digestEditing,
        } = this

        if (!filterEditing) {
            return
        }

        if (filterEditMode === 'create') {
            const newFilter = await this.createFilter({
                ...filterEditing,
                name,
                is_shared,
            })

            if (frequency) {
                await this.createDigest({
                    filter_id: newFilter.id,
                    frequency,
                    day,
                })
            }
        } else if (filterEditMode === 'edit') {
            await this.patchFilter({ ...filterEditing, name, is_shared })

            if (frequency && !digestEditing) {
                this.createDigest({
                    filter_id: filterEditing.id,
                    frequency,
                    day,
                })
            } else if (frequency && digestEditing) {
                this.patchDigest({
                    id: digestEditing.id,
                    filter_id: filterEditing.id,
                    frequency,
                    day,
                })
            } else if (!frequency && digestEditing) {
                this.deleteDigest(digestEditing.id)
            }
        }

        this.handleClose()
        this.statusAddToast(`'${name || 'filter'}' has been saved`)
    }

    public async handleDelete() {
        if (this.digestEditing) {
            await this.deleteDigest(this.digestEditing.id)
        }
        if (this.filterEditing) {
            await this.deleteFilter(this.filterEditing.id)
        }

        this.handleClose()
        this.statusAddToast(`'${this.name || 'filter'}' has been deleted`)
    }
}
